import React, { useState } from "react"

import { AlertCircle, CheckCircle2, FileText, Loader2, Upload, XCircle } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

interface ImportResult {
  success: boolean
  message?: string
  results?: {
    inserted: number
    total: number
    errors?: Array<{ row: number; error: string }>
    parseErrors?: Array<{ row: number; error: string }>
  }
  error?: string
}

const ImportDiaryEntries: React.FC = () => {
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        toast.error("Invalid file type", { description: "Please upload a CSV file" })
        return
      }
      setCsvFile(file)
      setResult(null)
    }
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!csvFile) {
      toast.error("No file selected", { description: "Please select a CSV file to upload" })
      return
    }

    setIsUploading(true)
    setResult(null)

    try {
      // Read CSV file content
      const csvData = await csvFile.text()

      // Get session token (optional since we removed auth, but keeping for consistency)
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      // Call the import function
      const functionUrl = `${getFunctionsBaseUrl()}/import-diary-entries`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ csvData }),
      })

      const data: ImportResult = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || `Failed to import CSV (status ${response.status})`)
      }

      setResult(data)
      toast.success("Import successful", {
        description: data.message || "Diary entries imported successfully",
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to import CSV"
      setResult({
        success: false,
        error: errorMessage,
      })
      toast.error("Import failed", { description: errorMessage })
    } finally {
      setIsUploading(false)
    }
  }

  const handleReset = () => {
    setCsvFile(null)
    setResult(null)
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Import Diary Entries</h1>
          <p className="text-muted-foreground">
            Upload a CSV file to import diary entries into Supabase
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>CSV File Upload</CardTitle>
            <CardDescription>
              Upload a Diary Entries Export CSV file. The file will be processed in the backend.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="csvFile" className="text-sm font-medium">
                    Select CSV File
                  </label>
                  <div className="border-2 border-dashed border-palette-jungleGreen300 rounded-lg p-6 text-center hover:border-palette-jungleGreen400 transition-colors">
                    <input
                      id="csvFile"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={isUploading}
                    />
                    <label htmlFor="csvFile" className="cursor-pointer">
                      <Upload className="mx-auto h-12 w-12 text-palette-jungleGreen400" />
                      <p className="mt-2 text-sm text-palette-jungleGreen600">
                        Click to upload or drag and drop your CSV file
                      </p>
                      <p className="text-xs text-palette-jungleGreen500 mt-1">CSV files only</p>
                    </label>
                  </div>
                  {csvFile && (
                    <div className="flex items-center gap-2 p-3 bg-palette-jungleGreen000 rounded-md">
                      <FileText className="h-5 w-5 text-palette-jungleGreen500" />
                      <span className="text-sm text-palette-jungleGreen700 flex-1">
                        {csvFile.name}
                      </span>
                      <span className="text-xs text-palette-jungleGreen500">
                        {(csvFile.size / 1024).toFixed(2)} KB
                      </span>
                      {!isUploading && (
                        <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
                          Remove
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {result && (
                  <div className="space-y-4">
                    {result.success ? (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                          <div className="flex-1">
                            <h3 className="font-medium text-green-900">Import Successful</h3>
                            {result.results && (
                              <div className="mt-2 text-sm text-green-700">
                                <p>
                                  <strong>{result.results.inserted}</strong> entries inserted
                                </p>
                                <p className="mt-1">
                                  <strong>Total:</strong> {result.results.total} entries processed
                                </p>
                              </div>
                            )}
                            {result.results?.errors && result.results.errors.length > 0 && (
                              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-sm font-medium text-yellow-900">
                                  {result.results.errors.length} error(s) occurred:
                                </p>
                                <ul className="mt-2 text-xs text-yellow-800 list-disc list-inside space-y-1">
                                  {result.results.errors.slice(0, 10).map((err, idx) => (
                                    <li key={idx}>
                                      Row {err.row}: {err.error}
                                    </li>
                                  ))}
                                  {result.results.errors.length > 10 && (
                                    <li>... and {result.results.errors.length - 10} more</li>
                                  )}
                                </ul>
                              </div>
                            )}
                            {result.results?.parseErrors &&
                              result.results.parseErrors.length > 0 && (
                                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                  <p className="text-sm font-medium text-yellow-900">
                                    {result.results.parseErrors.length} parse error(s):
                                  </p>
                                  <ul className="mt-2 text-xs text-yellow-800 list-disc list-inside space-y-1">
                                    {result.results.parseErrors.slice(0, 10).map((err, idx) => (
                                      <li key={idx}>
                                        Row {err.row}: {err.error}
                                      </li>
                                    ))}
                                    {result.results.parseErrors.length > 10 && (
                                      <li>... and {result.results.parseErrors.length - 10} more</li>
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                          <div className="flex-1">
                            <h3 className="font-medium text-red-900">Import Failed</h3>
                            <p className="mt-1 text-sm text-red-700">{result.error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    disabled={isUploading}
                  >
                    Reset
                  </Button>
                  <Button type="submit" disabled={!csvFile || isUploading}>
                    {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isUploading ? "Importing..." : "Import CSV"}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>CSV Format Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium">Expected Columns:</p>
                  <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-1">
                    <li>
                      <code>Entry_ID</code> - Entry identifier
                    </li>
                    <li>
                      <code>Engagement Tags</code> - Engagement tag
                    </li>
                    <li>
                      <code>Engagement Sub tag</code> - Engagement sub tag
                    </li>
                    <li>
                      <code>Notes</code> - Notes field
                    </li>
                    <li>
                      <code>Link to Driver</code> - Link to person (Airtable record ID)
                    </li>
                    <li>
                      <code>Call Date/ Time</code> - Call date/time
                    </li>
                    <li>
                      <code>Created Date/ Time</code> - Created date/time
                    </li>
                    <li>
                      <code>Follow up Action?</code> - Follow up action
                    </li>
                    <li>
                      <code>Actions Completed</code> - Action completed (true/false)
                    </li>
                    <li>
                      <code>Conducted By Email</code> - Conducted by email
                    </li>
                    <li>
                      <code>Location (from Link to Driver)</code> - Location
                    </li>
                    <li>
                      <code>sheriff_areas (from Link to Driver)</code> - Sheriff areas
                    </li>
                    <li>
                      <code>Employer (from Link to Driver)</code> - Employer
                    </li>
                    <li>
                      <code>sheriff_type (from Link to Driver)</code> - Sheriff type
                    </li>
                    <li>
                      <code>Driver Manager Team (from Link to Driver)</code> - Driver manager team
                    </li>
                    <li>
                      <code>Zendesk_ID_Formatted_String (from Link to Driver)</code> - Zendesk ID
                    </li>
                    <li>
                      <code>Person (from Link to Driver)</code> - Person ID for lookup
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium">Notes:</p>
                  <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-1">
                    <li>All processing happens in the backend</li>
                    <li>
                      <code>airtable_record_id</code> will be set to null for imported entries
                    </li>
                    <li>
                      If <code>Link to Driver</code> or <code>Person</code> is provided, the system
                      will look up the person and link the entry
                    </li>
                    <li>Date fields should be in ISO format or common date formats</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default ImportDiaryEntries
